const MAX_URLS = 8;
const SAFE_EAGERNESS = 'conservative';

const UNSAFE_PATH_PREFIXES = ['/cart/add', '/cart/change', '/cart/clear', '/cart/update', '/checkout'];

const UNSAFE_PATH_PATTERNS = [
  /^\/account\/logout(?:\/|$)/,
  /^\/account\/orders(?:\/|$)/,
];

class SpeculationRulesManager {
  #urls = [];
  #script = null;

  get supported() {
    return typeof HTMLScriptElement !== 'undefined' && typeof HTMLScriptElement.supports === 'function'
      ? HTMLScriptElement.supports('speculationrules')
      : false;
  }

  prefetch(input, eagerness = SAFE_EAGERNESS) {
    if (!this.supported) return;

    const candidates = Array.isArray(input) ? input : [input];
    let changed = false;

    for (const candidate of candidates) {
      const normalized = this.#normalize(candidate);
      if (!normalized || this.#urls.includes(normalized)) continue;

      this.#urls.push(normalized);
      changed = true;
    }

    if (!changed) return;

    if (this.#urls.length > MAX_URLS) {
      this.#urls.splice(0, this.#urls.length - MAX_URLS);
    }

    this.#render(eagerness);
  }

  speculateAnchors(anchors, limit = 4) {
    const urls = [];

    for (const anchor of anchors) {
      if (!(anchor instanceof HTMLAnchorElement)) continue;

      const normalized = this.#normalize(anchor.href);
      if (!normalized) continue;

      urls.push(normalized);
      if (urls.length >= limit) break;
    }

    this.prefetch(urls);
  }

  #normalize(candidate) {
    if (!candidate) return null;

    let url;
    try {
      url = candidate instanceof URL ? candidate : new URL(candidate, window.location.origin);
    } catch {
      return null;
    }

    // Only speculate same-origin document navigations.
    if (url.origin !== window.location.origin) return null;
    if (url.hash && `${url.pathname}${url.search}` === `${window.location.pathname}${window.location.search}`) return null;
    if (UNSAFE_PATH_PREFIXES.some((prefix) => url.pathname.startsWith(prefix))) return null;
    if (UNSAFE_PATH_PATTERNS.some((pattern) => pattern.test(url.pathname))) return null;
    if (url.searchParams.has('view') && url.searchParams.get('view') === 'ajax') return null;

    return url.toString();
  }

  #ensureScript() {
    if (this.#script instanceof HTMLScriptElement) return this.#script;

    const script = document.createElement('script');
    script.type = 'speculationrules';
    script.id = 'theme-speculation-rules';
    document.head.appendChild(script);
    this.#script = script;

    return script;
  }

  #render(eagerness) {
    const script = this.#ensureScript();
    script.textContent = JSON.stringify({
      prefetch: [
        {
          urls: this.#urls,
          eagerness,
        },
      ],
    });
  }
}

export const speculationRules = new SpeculationRulesManager();
